import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dvmExtractorService from '../services/dvmExtractorService';
import ConfirmModal from './shared/ConfirmModal';
import { formatDateTime } from '../utils/datetime';
import './AccountForm.css';
import { FileSpreadsheet } from 'lucide-react';
import Tabs from './shared/Tabs';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from './support/ProductSupportTab';

// One extractor: what it is set up to read, the Extract button, and the runs it has produced.
//
// EXTRACT IS A JOB. A property's DVM is one OHIP call per conversion code — minutes of work — so the
// button starts a run and the page polls it. Leaving the page does not cancel anything: the run finishes
// server-side and appears in the history with its file.

const FORMATS = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'json', label: 'JSON (.json)' },
];

const STATUS_LABEL = {
    pending: 'Queued',
    running: 'Running',
    done: 'Complete',
    error: 'Failed',
};

const DvmExtractorDetailPage = () => {
    const { id } = useParams();
    const [tab, setTab] = useSupportTab('extractor');   // Extractor | Support
    const navigate = useNavigate();

    const [extractor, setExtractor] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [format, setFormat] = useState('xlsx');
    // Settings are editable HERE rather than only at creation. The external system in particular is the
    // one field OHIP rejects outright ("Interface is not valid"), and without an edit the only way to
    // correct it was to delete the extractor and start again.
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [activeRun, setActiveRun] = useState(null);

    const loadRuns = useCallback(async () => {
        try {
            const data = await dvmExtractorService.getRuns(id);
            setRuns(data.runs || []);
        } catch (err) {
            console.error('Failed to load runs', err);
        }
    }, [id]);

    useEffect(() => {
        (async () => {
            try {
                const data = await dvmExtractorService.getById(id);
                setExtractor(data.dvmextractor);
                setFormat(data.dvmextractor?.format || 'xlsx');
                await loadRuns();
            } catch (err) {
                setError(err.response?.data?.error?.message || err.message || 'Could not load this extractor');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, loadRuns]);

    const startEdit = () => {
        setEdit({
            name: extractor.name || '',
            external_system: extractor.external_system || '',
            ohip_hotel_code: extractor.ohip_hotel_code || '',
            configured_only: extractor.configured_only || 'Y',
            format: extractor.format || 'xlsx',
        });
        setEditing(true);
    };

    const saveEdit = async () => {
        if (!edit.name?.trim()) { setError('Name is required'); return; }
        setSavingEdit(true);
        setError(null);
        try {
            const data = await dvmExtractorService.update(id, {
                name: edit.name.trim(),
                // Blank means "use the credential's own EXTERNALSYSTEM" — sent as null so it is cleared
                // rather than stored as an empty string that would fail the same way.
                external_system: edit.external_system.trim() || null,
                ohip_hotel_code: edit.ohip_hotel_code.trim() || null,
                configured_only: edit.configured_only,
                format: edit.format,
            });
            setExtractor(data.dvmextractor);
            setFormat(data.dvmextractor.format || 'xlsx');
            setEditing(false);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.message || 'Could not save');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleExtract = async () => {
        setExtracting(true);
        setError(null);
        setActiveRun(null);
        try {
            const { runId } = await dvmExtractorService.extract(id, format);
            await loadRuns();
            const finished = await dvmExtractorService.pollRun(runId, {
                onTick: (run) => { setActiveRun(run); loadRuns(); },
            });
            setActiveRun(finished);
            await loadRuns();
            // A run can finish with a file AND a warning (some codes unreadable), so the error text is
            // shown without hiding the download.
            if (finished.status === 'error') setError(finished.error || 'The extract failed.');
            else if (finished.error) setError(finished.error);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.message || 'The extract failed');
        } finally {
            setExtracting(false);
        }
    };

    const handleDelete = () => {
        setConfirmModal({
            title: 'Delete Extractor',
            message: `Delete "${extractor.name}"? Its extract history and every file it produced go with it. This cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                try {
                    await dvmExtractorService.delete(id);
                    navigate('/apps/dvm-extractor');
                } catch (err) {
                    setError(err.response?.data?.error?.message || err.message || 'Could not delete');
                }
            },
        });
    };

    const handleDownload = async (runId) => {
        try {
            await dvmExtractorService.download(runId);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.message || 'Could not download the file');
        }
    };

    if (loading) return <div className="account-form-container"><p>Loading…</p></div>;
    if (!extractor) return <div className="account-form-container"><div className="form-error">{error || 'Not found'}</div></div>;

    return (
        <div className="account-form-container">
            <div className="account-form-header">
                <h1>{extractor.name}</h1>
                <p className="no-data-hint" style={{ margin: '0.35rem 0 0' }}>
                    {extractor.hotelname || `Hotel ${extractor.hotel_id}`}
                    {extractor.external_system ? ` · ${extractor.external_system}` : ''}
                    {extractor.ohip_hotel_code ? ` · ${extractor.ohip_hotel_code}` : ''}
                </p>
            </div>

            {error && <div className="form-error">{error}</div>}

            <Tabs tabs={[{ key: 'extractor', label: 'Extractor', icon: <FileSpreadsheet size={20} /> }, SUPPORT_TAB]} active={tab} onChange={setTab} />

            {tab === 'support' ? (
                <ProductSupportTab product="DvmExtractor" productId={id} noun="this extractor"
                    context={{ screen: 'dvm-extractor', extractorId: id, extractorName: extractor.name,
                        externalSystem: extractor.external_system, ohipHotelCode: extractor.ohip_hotel_code }} />
            ) : (<>
            <div className="form-section">
                <div className="hotels-header with-title">
                    <h3 style={{ margin: 0 }}>Settings</h3>
                    {!editing && <button type="button" className="btn-secondary" onClick={startEdit}>Edit</button>}
                </div>
                {editing ? (
                    <>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Name <span className="required">*</span></label>
                                <input type="text" value={edit.name} onChange={e => setEdit(p => ({ ...p, name: e.target.value }))} />
                            </div>
                            <div className="form-group">
                                <label>External system</label>
                                <input
                                    type="text"
                                    value={edit.external_system}
                                    onChange={e => setEdit(p => ({ ...p, external_system: e.target.value }))}
                                    placeholder="Leave blank to use the credential's own setting"
                                />
                                <span className="no-data-hint">
                                    The interface CODE on the property (HCONNECT, OHIP-HCONNECT) — not a display name.
                                </span>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>OHIP hotel code</label>
                                <input
                                    type="text"
                                    value={edit.ohip_hotel_code}
                                    onChange={e => setEdit(p => ({ ...p, ohip_hotel_code: e.target.value }))}
                                    placeholder="Leave blank to use the credential's HOTELID"
                                />
                            </div>
                            <div className="form-group">
                                <label>Scope</label>
                                <select value={edit.configured_only} onChange={e => setEdit(p => ({ ...p, configured_only: e.target.value }))}>
                                    <option value="Y">Configured codes only (faster)</option>
                                    <option value="N">Every code in the catalogue</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-actions">
                            <button type="button" className="btn-primary" onClick={saveEdit} disabled={savingEdit}>
                                {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                            <button type="button" className="btn-secondary" onClick={() => { setEditing(false); setError(null); }}>Cancel</button>
                        </div>
                    </>
                ) : (
                    <p className="no-data-hint">
                        External system: {extractor.external_system || `(from the credential)`}
                        {' · '}OHIP hotel: {extractor.ohip_hotel_code || '(from the credential)'}
                        {' · '}Scope: {extractor.configured_only === 'N' ? 'every code in the catalogue' : 'configured codes only'}
                        {' · '}Credentials: {extractor.credential_name || `#${extractor.credential_id}`}
                    </p>
                )}
            </div>

            <div className="form-section">
                <h3>Extract</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label>Format</label>
                        <select value={format} onChange={e => setFormat(e.target.value)} disabled={extracting}>
                            {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                        <button type="button" className="btn-primary" onClick={handleExtract} disabled={extracting}>
                            {extracting ? 'Extracting…' : 'Extract'}
                        </button>
                    </div>
                </div>
                {extracting && (
                    <p className="no-data-hint">
                        Reading the mappings from OHIP — one call per conversion code, so this takes a few minutes.
                        {activeRun?.code_count ? ` ${activeRun.code_count} code(s) found.` : ''}
                        {' '}You can leave this page; the run carries on and appears below.
                    </p>
                )}
            </div>

            <div className="form-section">
                <h3>Runs</h3>
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Status</th>
                                <th>Codes</th>
                                <th>Rows</th>
                                <th>Format</th>
                                <th>File</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 ? (
                                <tr><td colSpan="6" className="no-data">
                                    <p>No extracts yet</p>
                                    <p className="no-data-hint">Press Extract to read this property’s mappings</p>
                                </td></tr>
                            ) : (
                                runs.map(run => (
                                    <tr key={run.id}>
                                        <td>{formatDateTime(run.started_at || run.created_at)}</td>
                                        <td>
                                            {STATUS_LABEL[run.status] || run.status}
                                            {run.error && <div className="no-data-hint" style={{ maxWidth: 420 }}>{run.error}</div>}
                                        </td>
                                        <td>{run.code_count ?? '—'}</td>
                                        <td>{run.row_count ?? '—'}</td>
                                        <td>{String(run.format || '').toUpperCase()}</td>
                                        <td>
                                            {run.has_file ? (
                                                <button type="button" className="btn-secondary" onClick={() => handleDownload(run.id)}>
                                                    Download
                                                </button>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => navigate('/apps/dvm-extractor')}>Back</button>
                {/* Deleting takes the runs with it (the FK cascades), which is why the confirm says so —
                    the files only exist on those rows. */}
                <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>
            </div>
            </>)}

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default DvmExtractorDetailPage;
